import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TabsContent, Tabs } from "@/components/ui/tabs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { GraduationCap, UserMinus, UserCheck, Users } from "lucide-react";
import {} from "lucide-react";
import { Input } from "../ui/input";
import { useSelector } from "react-redux";
import { RootState } from "@/types";
import { useGetAllFeeQuery } from "../../store/feeApi";
import { useGetAllQuery } from "../../store/studentApi";
import { useGetBatchQuery } from "../../store/batchApi";
import ExportReport from "./ExportReport";
import StatCard from "../StatCard";
// import Pagination from "../Pagination";

import { useGetAllExpanceQuery } from "@/store/expancesApi";
// import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import FeeTable from "./FeeTable";
import ExportDbDataBtn from "./ExportDbDataBtn";

const AdminDashboard = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortCriteria, setSortCriteria] = useState("");
  const { user } = useSelector((state: RootState) => state.user);
  const { data: allFee } = useGetAllFeeQuery(user?.token);
  const { data: allStudents } = useGetAllQuery(user?.token);
  const { data: allBatches } = useGetBatchQuery();
  const pendingFees = useMemo(() => {
    return allFee
      ?.filter((fee: Fee) => fee.payable !== fee.paid)
      ?.reduce((acc: number, fee: Fee) => acc + (fee.payable - fee.paid), 0);
  }, [allFee]);

  const { data: allExpanses } = useGetAllExpanceQuery(user?.token);

  const receivedFee = useMemo(() => {
    return allFee?.reduce((acc: number, fee: Fee) => acc + fee.paid, 0);
  }, [allFee]);

  // Mock student fee data

  const hasPendingFee = allFee?.filter((fee: Fee) => fee.isPayable == true);

  const filteredStudents = hasPendingFee?.filter(
    (fee: Fee) =>
      fee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fee.course.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fee.bCode.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const studentEnrollmentData = useMemo(() => {
    if (!allBatches) return [];

    return allBatches?.map((batch: Batch) => ({
      name: batch.bCode,
      totalStudents:
        allStudents?.filter((student: Student) => student.batch === batch.bCode)
          .length || 0,
      activeBatchStudents:
        allStudents?.filter(
          (student: Student) =>
            student.batch === batch.bCode &&
            student.isPlaced.toLowerCase() !== "yes" &&
            student.status.toLowerCase() === "active",
        )?.length || 0,
    }));
  }, [allBatches, allStudents]);

  const topEnrolledBatchData = useMemo(() => {
    if (!allBatches || !allStudents) return [];

    // Calculate batch statistics
    const batchStats = allBatches.map((batch: Batch) => {
      const batchStudents = allStudents.filter(
        (student: Student) => student.batch === batch.bCode,
      );

      const placedStudentsCount = batchStudents.filter(
        (student: Student) => student.isPlaced.toLowerCase() === "yes",
      ).length;

      const activeBatchStudentsCount = batchStudents.filter(
        (student: Student) =>
          student.isPlaced.toLowerCase() !== "yes" &&
          student.status.toLowerCase() === "active",
      ).length;

      return {
        name: batch.bCode,
        totalStudents: batchStudents.length,
        activeBatchStudents: activeBatchStudentsCount,
        placedStudents: placedStudentsCount,
        growth: batchStudents.length
          ? Math.round((placedStudentsCount / batchStudents.length) * 100)
          : 0,
      };
    });

    // Sort based on selected criteria
    return batchStats
      .sort((a, b) => {
        switch (sortCriteria) {
          case "totalStudents":
            return b.totalStudents - a.totalStudents;
          case "activeBatchStudents":
            return b.activeBatchStudents - a.activeBatchStudents;
          case "placedStudents":
            return b.placedStudents - a.placedStudents;
          case "growth":
            return b.growth - a.growth;
          default:
            return b.totalStudents - a.totalStudents;
        }
      })
      .slice(0, 5); // Top 5 batches
  }, [allBatches, allStudents, sortCriteria]);

  // Dynamic order statistics (using student placement as a proxy for "orders")
  const orderStatisticsData = useMemo(() => {
    if (!allBatches) return [];

    return allBatches.map((batch: Batch) => ({
      name: batch.bCode,
      placed:
        allStudents?.filter(
          (student: Student) =>
            student.batch === batch.bCode &&
            student.isPlaced.toLowerCase() === "yes",
        ).length || 0,
      pending:
        allStudents?.filter(
          (student: Student) =>
            student.batch === batch.bCode &&
            student.isPlaced.toLowerCase() === "no",
        ).length || 0,
      dropped:
        allStudents?.filter(
          (student: Student) =>
            student.batch === batch.bCode &&
            student.status.toLowerCase() === "disable",
        ).length || 0,
    }));
  }, [allBatches, allStudents]);

  const totalExpanse = allExpanses?.reduce((a, p) => a + p.amount, 0);

  return (
    <div className="p-6 bg-card min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-foreground">
          Welcome Back, {user?.name || "Admin"}
        </h1>
        <div className="flex gap-4 mt-4 md:mt-0">
          <Button className="px-4 hidden sm:block py-2 bg-gray-400 rounded-lg hover:bg-gray-300 transition-colors">
            Jan 01, 2025 to {new Date().toLocaleDateString("en-IN")}
          </Button>
          <ExportReport />
          <ExportDbDataBtn />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Students"
          value={allStudents?.length}
          icon={GraduationCap}
          color="text-red-600"
          // trend={5.2}
          link={"/students"}
        />
        <StatCard
          title="Active Students"
          value={
            allStudents?.filter(
              (std: Student) => std.status.toLocaleLowerCase() == "active",
            )?.length
          }
          icon={Users}
          color="text-blue-600"
          // trend={2.1}
          link={"/students?status=Active"}
        />
        <StatCard
          title="Dropped Students"
          value={
            allStudents?.filter(
              (std: Student) => std.status.toLocaleLowerCase() == "disable",
            )?.length
          }
          icon={UserMinus}
          color="text-orange-600"
          // trend={5}
          link={"/students?status=Disable"}
        />

        <StatCard
          title="Total Batch"
          value={allBatches?.length}
          icon={UserCheck}
          color="text-red-600"
          // trend={1}
          link={"/batch"}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Balance"
          value={receivedFee - totalExpanse}
          icon={GraduationCap}
          color={totalExpanse > receivedFee ? "text-red-500" : "text-red-600"}
          // trend={5.2}
          // link={"/"}
        />
        <StatCard
          title="Total Expense"
          value={totalExpanse}
          icon={Users}
          color="text-red-600"
          // trend={-2.1}
          link={"/expenses"}
        />
        <StatCard
          title="Total Received Fees"
          value={receivedFee}
          icon={UserMinus}
          color="text-red-600"
          // trend={10}
          link={"/view-fee"}
        />

        <StatCard
          title="Placed Students"
          value={
            allStudents?.filter(
              (std: Student) => std.isPlaced.toLocaleLowerCase() == "yes",
            )?.length
          }
          icon={UserCheck}
          color="text-red-600"
          // trend={10}
          link={"/students?status=yes"}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="bg-card">
          <CardHeader>
            <CardTitle>
              <h1>Active Student</h1>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={studentEnrollmentData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar
                  dataKey="totalStudents"
                  fill="#15A249"
                  name="Total Students"
                />
                <Bar
                  dataKey="activeBatchStudents"
                  fill="#82ca9d"
                  name="Active Students"
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-card">
          <CardHeader>
            <CardTitle>
              <h1>Batch Placement Statistics</h1>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="batch" className="w-full">
              {/* <TabsList className="mb-4">
                <TabsTrigger value="batch">Batch View</TabsTrigger>
              </TabsList> */}
              <TabsContent value="batch">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={orderStatisticsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar
                      dataKey="placed"
                      fill="#15A249"
                      name="Placed Students"
                    />
                    <Bar
                      dataKey="pending"
                      fill="#ff7300"
                      name="Pending Placement"
                    />
                    <Bar
                      dataKey="dropped"
                      fill="#ff4d4d"
                      name="Dropped Students"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Pending Fees Section */}
      <Card className="mb-8">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>
              <h1>Students who have pending fees</h1>
            </CardTitle>
            <Input
              placeholder="Search by Student Name or Course or Batch"
              className="max-w-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardHeader>
        {/* <CardContent>
          <div className="overflow-x-auto">
            <Pagination data={filteredStudents || []} itemsPerPage={10}>
              {(paginatedStudents) => (
                <table className="w-full border-collapse">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        Student Name
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        Batch Code
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        Course Name
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        Paid Amount
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        Pending Amount
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        Payable Amount
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        Fee Records
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y ">
                    {paginatedStudents?.map((fee: Fee) => (
                      <tr key={fee._id} className="hover:bg-muted">
                        <td className="px-4 py-3 text-sm textforeground">
                          {fee.name}
                        </td>
                        <td className="px-4 py-3 text-sm textforeground">
                          {fee.bCode}
                        </td>
                        <td className="px-4 py-3 text-sm textforeground">
                          {fee.course}
                        </td>
                        <td className="px-4 py-3 text-sm text-primary">
                          ₹{fee.paid?.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-red-600">
                          ₹{(fee.payable - fee.paid).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm textforeground">
                          ₹{fee.payable?.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Link
                            to={`/feehistory/${fee.student}`}
                            className="text-blue-600 hover:underline"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Pagination>
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm font-medium">
              <span>Total Pending Amount:</span>
              <span className="text-red-600">
                ₹{pendingFees?.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-sm font-medium">
              <span>Total Received Amount:</span>
              <span className="text-primary">
                ₹{receivedFee?.toLocaleString()}
              </span>
            </div>
          </div>
        </CardContent> */}
        <FeeTable
          filteredStudents={filteredStudents}
          pendingFees={pendingFees}
          receivedFee={receivedFee}
        />
      </Card>

      {/* Categories Section */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex-grow w-full">
              <h1>Top Enrolled Batches</h1>
            </CardTitle>

            <div>
              <Select
                value={sortCriteria}
                onValueChange={(value) => setSortCriteria(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Sort Criteria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="totalStudents">Total Students</SelectItem>
                  <SelectItem value="activeBatchStudents">
                    Active Students
                  </SelectItem>
                  <SelectItem value="placedStudents">
                    Placed Students
                  </SelectItem>
                  <SelectItem value="growth">Placement Growth</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {topEnrolledBatchData?.map((batch, index: number) => (
              <div
                key={batch.name}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: `hsl(${index * 60}, 70%, 50%)`,
                    }}
                  ></div>
                  <div>
                    <p className="font-medium">{batch.name}</p>
                    <p
                      className={`text-sm ${
                        batch.growth >= 0 ? "text-red-500" : "text-red-500"
                      }`}
                    >
                      {batch.growth >= 0 ? "+" : ""}
                      {batch.growth}% Placement
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <p className="font-medium text-sm text-gray-600">
                    Total: {batch.totalStudents}
                  </p>
                  <p className="font-medium text-sm text-blue-600">
                    Placed: {batch.placedStudents}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
